import { createTheme } from "@mui/material/styles";

export const theme = {
  colors: {
    // Primary colors
    primary: "#0066cc",
    primaryHover: "#0052a3",

    // Accent colors
    limeGreen: "#E4FF97",
    darkLimeGreen: "#D2F400",
    limeGreenFaded: "rgba(211, 243, 0, 0.8)",
    lightLime: "#F7FEE0",
    mintGreen: "#EFFAE5",
    purple: "#6A63F5",
    mediumPurple: "#ADA9F9",
    lightPurple: "#F0EFFE",
    blue: "#007aff",

    // Feedback colors
    successGreen: "#4ade80",
    errorRed: "#ef4444",

    // Neutrals
    black: "#000",
    white: "#fff",
    gray: {
      50: "#f5f5f5",
      100: "#f0f0f0",
      200: "#e0e0e0",
      300: "#ccc",
      400: "#999",
      500: "#666",
      600: "#333",
    },

    // Backgrounds
    background: {
      default: "#ffffff",
      paper: "#fff",
    },

    // Code blocks
    code: {
      background: "#1e1e1e",
      text: "#d4d4d4",
    },
  },

  fonts: {
    // Font families - these reference CSS variables set by next/font/google
    sans: "var(--font-inter), system-ui, sans-serif",
    serif: "var(--font-kopub-batang), serif",

    // Font sizes
    sizes: {
      xs: "13px",
      sm: "14px",
      base: "15px",
      md: "16px",
      lg: "17px",
      xl: "20px",
      "2xl": "22px",
      "3xl": "24px",
      "4xl": "28px",
      "5xl": "32px",
      "6xl": "36px",
      "7xl": "48px",
      "8xl": "56px",
    },

    // Font weights
    weights: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },

  // MUI border radius units (1 unit = 8px)
  borderRadius: {
    sm: 0.5, // 4px
    md: 1, // 8px
    lg: 3, // 24px
  },

  // Container max-widths
  containerWidth: {
    page: "1280px",
    content: "900px",
    inset: "75%",
  },

  // Patterns
  patterns: {
    dots: {
      backgroundImage: `radial-gradient(circle, #e0e0e0 1.25px, transparent 1.25px)`,
      backgroundSize: "20px 20px",
    },
    whiteDotsTransparent: {
      backgroundImage: `radial-gradient(circle, rgba(255, 255, 255, 0.8) 2px, transparent 2px)`,
      backgroundSize: "20px 20px",
    },
  },

  breakpoints: {
    mobile: "0px",
    desktop: "600px",
  },
} as const;

export type Theme = typeof theme;

export const muiTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 600,
      lg: 600,
      xl: 600,
    },
  },
  typography: {
    fontFamily: theme.fonts.sans,
    // H1: Mobile 36px, Desktop 64px - bold Inter
    h1: {
      fontFamily: theme.fonts.sans,
      fontSize: "36px",
      "@media (min-width:600px)": {
        fontSize: "64px",
      },
      fontWeight: theme.fonts.weights.bold,
      lineHeight: 1.1,
      letterSpacing: "-0.02em",
    },
    // H2: Mobile 36px, Desktop 64px - bold KoPub Batang
    h2: {
      fontFamily: theme.fonts.serif,
      fontSize: "36px",
      fontWeight: theme.fonts.weights.bold,
      lineHeight: 1.1,
      marginTop: "48px",
      marginBottom: "16px",
      "@media (min-width:600px)": {
        fontSize: "64px",
        marginTop: "80px",
        marginBottom: "24px",
      },
    },
    // H3: Mobile 28px, Desktop 48px - bold Inter
    h3: {
      fontFamily: theme.fonts.sans,
      fontSize: "28px",
      fontWeight: theme.fonts.weights.bold,
      lineHeight: 1.2,
      marginTop: "16px",
      marginBottom: "8px",
      "@media (min-width:600px)": {
        fontSize: "48px",
        marginBottom: "16px",
      },
    },
    // H4: Mobile 24px, Desktop 36px - bold Inter
    h4: {
      fontFamily: theme.fonts.sans,
      fontSize: "24px",
      fontWeight: theme.fonts.weights.bold,
      lineHeight: 1.3,
      marginTop: "16px",
      marginBottom: "8px",
      "@media (min-width:600px)": {
        fontSize: "36px",
        marginTop: "32px",
        marginBottom: "16px",
      },
    },
    // H5: Mobile 20px, Desktop 28px - bold Inter
    h5: {
      fontFamily: theme.fonts.sans,
      fontSize: "20px",
      fontWeight: theme.fonts.weights.bold,
      lineHeight: 1.3,
      marginTop: "8px",
      marginBottom: "6px",
      "@media (min-width:600px)": {
        fontSize: "24px",
        marginTop: "24px",
        marginBottom: "12px",
      },
    },
    // H6: Mobile 18px, Desktop 22px - bold Inter
    h6: {
      fontFamily: theme.fonts.serif,
      fontSize: "18px",
      fontWeight: theme.fonts.weights.bold,
      lineHeight: 1.4,
      marginTop: "8px",
      marginBottom: "4px",
      "@media (min-width:600px)": {
        fontSize: "22px",
        marginTop: "16px",
        marginBottom: "8px",
      },
    },
    // Body: Mobile 16px, Desktop 18px - Inter Medium
    body1: {
      fontFamily: theme.fonts.sans,
      fontSize: "16px",
      fontWeight: theme.fonts.weights.medium,
      lineHeight: 1.45,
      letterSpacing: "-0.005em",
      color: theme.colors.gray[600],
      marginBottom: "18px",
      "@media (min-width:600px)": {
        fontSize: "18px",
        marginBottom: "24px",
      },
    },
    // Large Body: Mobile 18px, Desktop 20px - Inter Medium
    body2: {
      fontFamily: theme.fonts.sans,
      fontSize: "18px",
      fontWeight: theme.fonts.weights.medium,
      lineHeight: 1.45,
      letterSpacing: "-0.005em",
      color: theme.colors.gray[600],
      "@media (min-width:600px)": {
        fontSize: "20px",
      },
    },
    // Additional semantic variants
    subtitle1: {
      fontFamily: theme.fonts.sans,
      fontSize: "14px",
      fontWeight: theme.fonts.weights.medium,
      lineHeight: 1.5,
      "@media (min-width:600px)": {
        fontSize: "16px",
      },
    },
    subtitle2: {
      fontFamily: theme.fonts.sans,
      fontSize: "13px",
      fontWeight: theme.fonts.weights.medium,
      lineHeight: 1.5,
      "@media (min-width:600px)": {
        fontSize: "14px",
      },
    },
    caption: {
      fontFamily: theme.fonts.sans,
      fontSize: "12px",
      lineHeight: 1.4,
      "@media (min-width:600px)": {
        fontSize: "13px",
      },
    },
  },
  palette: {
    primary: {
      main: theme.colors.primary,
    },
    text: {
      primary: theme.colors.black,
      secondary: theme.colors.gray[600],
    },
    background: {
      default: theme.colors.background.default,
      paper: theme.colors.background.paper,
    },
  },
});
